import React from 'react';
import styled from '@emotion/styled';
import { css, SerializedStyles } from '@emotion/react';
import { Radio, RadioGroup, FormControlLabel, RadioProps as MuiRadioProps } from '@mui/material';
import { aigleColor } from '@styles/colorStyleGuide';

/**
 * AigleRadio Props
 */
export interface AigleRadioProps extends Omit<MuiRadioProps, 'onChange'> {
  /** Label 텍스트 */
  label?: string;
  /** Radio value */
  value: string | number;
  /** 변경 이벤트 핸들러 */
  onChange?: (value: string | number, event: React.ChangeEvent<HTMLInputElement>) => void;
  /** 체크 아이콘 (커스텀) */
  checkedIcon?: React.ReactNode;
  /** 언체크 아이콘 (커스텀) */
  icon?: React.ReactNode;
  /** 에러 상태 여부 */
  error?: boolean;
  /** 컨테이너 추가 스타일 */
  containerStyle?: SerializedStyles;
  /** 리플 효과 비활성화 */
  disableRipple?: boolean;
}

/**
 * AigleRadioGroup Props
 */
export interface AigleRadioGroupProps {
  /** Radio 그룹 이름 */
  name?: string;
  /** 선택된 값 */
  value?: string | number;
  /** 기본 선택 값 */
  defaultValue?: string | number;
  /** 변경 이벤트 핸들러 */
  onChange?: (value: string | number) => void;
  /** Radio 버튼들 */
  children: React.ReactNode;
  /** 비활성화 여부 */
  disabled?: boolean;
  /** 방향 */
  direction?: 'horizontal' | 'vertical';
  /** Gap 크기 */
  gap?: number;
}

// Styled Components
const StyledFormControlLabel = styled(FormControlLabel)<{
  $error?: boolean;
  $containerStyle?: SerializedStyles;
}>`
  margin: 0;
  display: flex;
  align-items: center;
  user-select: none;
  
  .MuiRadio-root {
    padding: 0 8px 0 0;
    
    svg {
      width: 20px;
      height: 20px;
    }
  }

  .MuiTypography-root {
    font-size: 14px;
    color: ${aigleColor.text.secondary};
    line-height: 1.5;
  }
  
  ${({ $error }) => $error && css`
    .MuiRadio-root {
      color: ${aigleColor.semantic.error};
    }
  `}
  
  &.Mui-disabled {
    .MuiTypography-root {
      color: ${aigleColor.text.disabled};
    }
  }
  
  ${({ $containerStyle }) => $containerStyle}
`;

const StyledRadio = styled(Radio)`
  &.MuiRadio-root {
    color: ${aigleColor.border.default};
    transition: all 0.2s ease;
    
    &:hover {
      background-color: ${aigleColor.alpha.black.alpha5};
    }
    
    &.Mui-checked {
      color: ${aigleColor.brand.primary};
    }
    
    &.Mui-disabled {
      color: ${aigleColor.text.disabled};
      opacity: 0.6;
    }
  }
`;

const StyledRadioGroup = styled(RadioGroup)<{
  $direction: 'horizontal' | 'vertical';
  $gap: number;
}>`
  display: flex;
  flex-direction: ${({ $direction }) => $direction === 'horizontal' ? 'row' : 'column'};
  gap: ${({ $gap }) => $gap}px;
  
  &.MuiFormGroup-root {
    flex-direction: ${({ $direction }) => $direction === 'horizontal' ? 'row' : 'column'};
  }
`;

// 기본 라디오 아이콘 (선택됨)
const DefaultCheckedRadioIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="9" fill={aigleColor.brand.primary}/>
    <path
      d="M8 12L10.5 14.5L16 9"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// 기본 라디오 아이콘 (선택 안됨)
const DefaultUncheckedRadioIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle
      cx="12"
      cy="12"
      r="8.5"
      fill="white"
      stroke={aigleColor.border.default}
    />
    <path
      d="M8 12L10.5 14.5L16 9"
      stroke={aigleColor.grey.light}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * AigleRadio Component
 * 
 * MUI Radio를 기반으로 한 일관된 디자인의 Radio 컴포넌트
 * 리플 효과와 애니메이션으로 더 나은 인터랙션을 제공합니다.
 * 
 * @example
 * // Basic Radio
 * <AigleRadio 
 *   name="option" 
 *   value="1" 
 *   label="옵션 1" 
 * />
 * 
 * @example
 * // With AigleRadioGroup
 * <AigleRadioGroup name="options" value={value} onChange={setValue}>
 *   <AigleRadio value="1" label="옵션 1" />
 *   <AigleRadio value="2" label="옵션 2" />
 * </AigleRadioGroup>
 */
export const AigleRadio = React.forwardRef<HTMLButtonElement, AigleRadioProps>(
  (
    {
      label,
      value,
      checked,
      onChange,
      checkedIcon,
      icon,
      disabled = false,
      error = false,
      containerStyle,
      disableRipple = false,
      ...props
    },
    ref
  ) => {
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(value, event);
    };
    
    const radioControl = (
      <StyledRadio
        ref={ref}
        value={value}
        checked={checked}
        onChange={handleChange}
        disabled={disabled}
        disableRipple={disableRipple}
        icon={icon || <DefaultUncheckedRadioIcon />}
        checkedIcon={checkedIcon || <DefaultCheckedRadioIcon />}
        {...props}
      />
    );
    
    // label이 없으면 Radio만 반환
    if (!label) {
      return radioControl;
    }
    
    return (
      <StyledFormControlLabel
        control={radioControl}
        label={label}
        disabled={disabled}
        $error={error}
        $containerStyle={containerStyle}
      />
    );
  }
);

AigleRadio.displayName = 'AigleRadio';

/**
 * AigleRadioGroup Component
 * 
 * MUI RadioGroup을 기반으로 Radio 버튼들을 그룹화하고 관리하는 컴포넌트
 * 
 * @example
 * // Radio Group (Vertical)
 * const [value, setValue] = useState('1');
 * 
 * <AigleRadioGroup 
 *   name="options" 
 *   value={value} 
 *   onChange={setValue}
 * >
 *   <AigleRadio value="1" label="옵션 1" />
 *   <AigleRadio value="2" label="옵션 2" />
 *   <AigleRadio value="3" label="옵션 3" />
 * </AigleRadioGroup>
 * 
 * @example
 * // Radio Group (Horizontal)
 * <AigleRadioGroup 
 *   name="options" 
 *   direction="horizontal"
 *   gap={16}
 * >
 *   <AigleRadio value="yes" label="예" />
 *   <AigleRadio value="no" label="아니오" />
 * </AigleRadioGroup>
 */
export const AigleRadioGroup: React.FC<AigleRadioGroupProps> = ({
  name,
  value,
  defaultValue,
  onChange,
  children,
  disabled = false,
  direction = 'vertical',
  gap = 12,
}) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    // 숫자로 변환 가능하면 숫자로, 아니면 문자열로
    const parsedValue = isNaN(Number(newValue)) ? newValue : Number(newValue);
    onChange?.(parsedValue);
  };
  
  // children에 disabled prop 전달
  const enhancedChildren = React.Children.map(children, (child) => {
    if (React.isValidElement(child) && child.type === AigleRadio) {
      return React.cloneElement(child as React.ReactElement<AigleRadioProps>, {
        disabled: disabled || child.props.disabled,
      });
    }
    return child;
  });
  
  return (
    <StyledRadioGroup
      name={name}
      value={value}
      defaultValue={defaultValue}
      onChange={handleChange}
      $direction={direction}
      $gap={gap}
    >
      {enhancedChildren}
    </StyledRadioGroup>
  );
};

AigleRadioGroup.displayName = 'AigleRadioGroup';

export default AigleRadio;

